<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useQueryClient } from '@tanstack/vue-query';
import { useToast } from 'vue-toastification';
import { getAccountMapOverlays, updateMapOverlay } from '@gpx-spa/queries';
import { setTitle } from '@/utils/title';
import { COMMON_SAVE_SUCCESS, COMMON_SAVE_ERROR } from '@/config/constants';
import ThreeColumnLayout from '@/components/_common/ThreeColumnLayout.vue';
import MapControlPanel from '@/components/_common/navbar/MapControlPanel.vue';
import CommonGoBack from '@/components/_common/CommonGoBack.vue';
import { topBarInsetClass } from '@/components/_common/navbar/topBarInset';
import OverlayEditor from '@/components/overlay/OverlayEditor.vue';
import OverlayEditorMap from '@/components/overlay/OverlayEditorMap.vue';
import { normalizeCorners } from '@/utils/map/overlayCorners';

import { useSynchronizedSidebars } from '@/composables/useSynchronizedSidebars';
useSynchronizedSidebars();

import { useResetChatOnSidebarClose } from '@/composables/useResetChatOnSidebarClose';
useResetChatOnSidebarClose();

import { useResetChatOnAccountOrUserChange } from '@/composables/useResetChatOnAccountOrUserChange';
useResetChatOnAccountOrUserChange();

const store = useStore();
const route = useRoute();
const router = useRouter();
const toast = useToast();
const queryClient = useQueryClient();

const accountId = computed(() => store.getters['auth/accountId']);
const overlayId = computed(() => +route.params.id);

const { data: overlaysData } = getAccountMapOverlays(accountId);
const overlay = computed(
  () => overlaysData.value?.find((o) => o.id === overlayId.value) || null,
);

const { mutateAsync: updateMutation } = updateMapOverlay();

const imageUrl = ref(null);
const corners = ref(null);
const opacity = ref(1);
const isSaving = ref(false);
const initialized = ref(false);

watch(
  overlaysData,
  (data) => {
    if (data && !overlay.value) {
      router.push({ name: 'OverlayListView' });
    }
  },
  { immediate: true },
);

watch(
  overlay,
  (val) => {
    if (val && !initialized.value) {
      imageUrl.value = val.file?.url || null;
      corners.value = val.corners
        ? {
            topLeft: { ...val.corners.topLeft },
            topRight: { ...val.corners.topRight },
            bottomLeft: { ...val.corners.bottomLeft },
          }
        : null;
      opacity.value = val.opacity ?? 1;
      initialized.value = true;
    }
  },
  { immediate: true },
);

function computeBottomRight(c) {
  return {
    lat: c.bottomLeft.lat + (c.topRight.lat - c.topLeft.lat),
    lng: c.bottomLeft.lng + (c.topRight.lng - c.topLeft.lng),
  };
}

async function handleSave({ name, opacity: opacityVal, corners: c }) {
  isSaving.value = true;
  try {
    const normalizedCorners = normalizeCorners(c);
    if (!normalizedCorners) {
      toast.error('Invalid overlay corners. Please re-upload the image and adjust corners.');
      return;
    }

    const bottomRight = computeBottomRight(normalizedCorners);
    if (!Number.isFinite(bottomRight.lat) || !Number.isFinite(bottomRight.lng)) {
      toast.error('Invalid overlay corners. Please adjust corners and try again.');
      return;
    }

    await updateMutation({
      accountId: accountId.value,
      id: overlayId.value,
      data: {
        name,
        opacity: opacityVal,
        corners: {
          topLeft: normalizedCorners.topLeft,
          topRight: normalizedCorners.topRight,
          bottomLeft: normalizedCorners.bottomLeft,
          bottomRight,
        },
      },
    });
    await queryClient.invalidateQueries({ queryKey: ['account-map-overlays'] });
    toast.success(COMMON_SAVE_SUCCESS);
    router.push({ name: 'OverlayListView' });
  } catch (e) {
    console.error('Overlay update error:', e);
    toast.error(COMMON_SAVE_ERROR);
  } finally {
    isSaving.value = false;
  }
}

function handleCancel() {
  router.push({ name: 'OverlayListView' });
}

setTitle('Edit Map Overlay');
</script>

<template>
  <ThreeColumnLayout>
    <template #leftTop>
      <div :class="topBarInsetClass">
        <CommonGoBack label="All Overlays" @click="handleCancel" />
      </div>
    </template>
    <template #left>
      <OverlayEditor
        v-if="overlay"
        :overlay="overlay"
        :corners="corners"
        :isSaving="isSaving"
        @save="handleSave"
        @cancel="handleCancel"
        @update:imageUrl="imageUrl = $event"
        @update:opacity="opacity = $event"
      />
    </template>
    <template #rightTop>
      <MapControlPanel />
    </template>
    <template #right>
      <OverlayEditorMap
        v-if="initialized"
        :imageUrl="imageUrl"
        :corners="corners"
        :opacity="opacity"
        @update:corners="corners = $event"
      />
    </template>
  </ThreeColumnLayout>
</template>
